import * as Sentry from '@sentry/react';

const ID_KEY = 'id';
const EMAIL_KEY = 'email';

export interface CurrentUser {
    id?: string;
    email?: string;
}

// Blank / whitespace-only values used to be written to localStorage as a
// fallback, which reached Sentry as an empty `email` field. Treat them as absent.
function normalize(value: string | null | undefined): string | undefined {
    const trimmed = value?.trim();
    return trimmed ? trimmed : undefined;
}

function readItem(key: string): string | undefined {
    try {
        return normalize(localStorage.getItem(key));
    } catch {
        return undefined;
    }
}

function writeItem(key: string, value: string): void {
    try {
        localStorage.setItem(key, value);
    } catch {
        // Storage unavailable (private mode, blocked cookies) — setCurrentUser
        // pushes the in-memory values to Sentry regardless of the write.
    }
}

function removeItem(key: string): void {
    try {
        localStorage.removeItem(key);
    } catch {
        // Nothing to clean up if storage is unavailable.
    }
}

export function getCurrentUser(): CurrentUser {
    return {
        id: readItem(ID_KEY),
        email: readItem(EMAIL_KEY),
    };
}

/**
 * Persists whatever identity we managed to resolve and pushes it onto the Sentry
 * scope, so errors, logs and session replays are all attributed to a real person.
 * Partial updates are fine — a missing field never clears an already known one.
 */
export function setCurrentUser({ id, email }: CurrentUser): void {
    const nextId = normalize(id);
    const nextEmail = normalize(email);

    if (nextId) {
        writeItem(ID_KEY, nextId);
    }
    if (nextEmail) {
        writeItem(EMAIL_KEY, nextEmail);
    }

    // The values we were just handed win; stored ones only fill in what is
    // missing, so an unavailable localStorage never costs us a fresh identity.
    const stored = getCurrentUser();
    const user = {
        id: nextId ?? stored.id,
        email: nextEmail ?? stored.email,
    };
    if (user.id || user.email) {
        Sentry.setUser(user);
    }
}

export function clearCurrentUser(): void {
    removeItem(ID_KEY);
    removeItem(EMAIL_KEY);
    Sentry.setUser(null);
}
